/** Preserve taxonomy/code identity while using the authenticated nested API. */
import type { DataProvider, Identifier, RaRecord } from 'react-admin';
import { apiFetch } from './api-rest-provider';
import { getListFilter, getListSort } from './list-utils';
type TaxonomyResource = 'taxonomies' | 'taxonomy_terms';
type TaxonomyApiRecord = Record<string, unknown> & {
  taxonomyKey?: string;
  code?: string;
  archived?: boolean;
};
const resourceName = (resource: string): TaxonomyResource => {
  if (resource === 'taxonomies' || resource === 'taxonomy_terms') return resource;
  throw new Error(`No authorized taxonomy API contract for resource: ${resource}`);
};
const key = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) throw new Error('Select a taxonomy and valid code');
  return encodeURIComponent(value);
};
const termIdentity = (id: Identifier): [string, string] => {
  try {
    const parsed = JSON.parse(String(id));
    if (Array.isArray(parsed) && parsed.length === 2 && parsed.every(v => typeof v === 'string' && v)) return parsed as [string,string];
  } catch { /* unknown or old IDs fail closed */ }
  throw new Error('Term identity must include its taxonomy and code');
};
const path = (resource: string, id: Identifier): string => {
  if (resourceName(resource) === 'taxonomies') return `taxonomies/${key(String(id))}`;
  const [taxonomy, code] = termIdentity(id);
  return `taxonomies/${key(taxonomy)}/terms/${key(code)}`;
};
const apiRecord = (value: unknown): TaxonomyApiRecord => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Taxonomy API returned an invalid record');
  }
  return value as TaxonomyApiRecord;
};
const apiRecords = (value: unknown): TaxonomyApiRecord[] => {
  if (!Array.isArray(value)) throw new Error('Taxonomy API returned an invalid list');
  return value.map(apiRecord);
};
const normalise = (resource: string, value: unknown, taxonomy?: string): RaRecord => {
  resourceName(resource);
  const item = apiRecord(value);
  const taxonomyKey = taxonomy ?? item.taxonomyKey;
  key(taxonomyKey);
  if (resource === 'taxonomy_terms') key(item.code);
  return { ...item, taxonomyKey, id: resource === 'taxonomies' ? taxonomyKey! : JSON.stringify([taxonomyKey, item.code]),
    archived_at: item.archived ? 1 : undefined };
};
const body = (data: Record<string, unknown>) => {
  const rest = { ...data };
  const archivedAt = rest.archived_at;
  for (const field of ['id', 'pk', 'sk', 'archived_at', 'taxonomyKey', 'code']) delete rest[field];
  return { ...rest, ...(Object.hasOwn(data, 'archived_at') ? { archived: !!archivedAt } : {}) };
};
export const taxonomyProvider: DataProvider = {
  getList: async (resource, params) => {
    resourceName(resource);
    const { filter = {}, sort = { field: 'label', order: 'ASC' }, pagination = { page: 1, perPage: 25 }, meta } = params;
    let items: RaRecord[];
    if (resource === 'taxonomies') items = apiRecords(await apiFetch('taxonomies')).map(item => normalise(resource, item));
    else {
      const selected = meta?.taxonomyKey ?? filter.taxonomyKey;
      const taxonomyKeys = selected
        ? [String(selected)]
        : apiRecords(await apiFetch('taxonomies')).map(item => {
            key(item.taxonomyKey);
            return item.taxonomyKey!;
          });
      const groups = await Promise.all(taxonomyKeys.map(async taxonomyKey =>
        apiRecords(await apiFetch(`taxonomies/${key(taxonomyKey)}/terms`)).map(item => normalise(resource, item, taxonomyKey))));
      items = groups.flat();
    }
    const filtered = items.filter(getListFilter(filter)).sort(getListSort(sort));
    return { data: filtered.slice((pagination.page - 1)*pagination.perPage, pagination.page*pagination.perPage) as never, total: filtered.length };
  },
  getOne: async (resource, params) => {
    resourceName(resource);
    const item = await apiFetch(path(resource, params.id));
    return { data: normalise(resource, item, resource === 'taxonomy_terms' ? termIdentity(params.id)[0] : undefined) as never };
  },
  getMany: async (resource, params) => {
    resourceName(resource);
    return { data: await Promise.all(params.ids.map(async id => (await taxonomyProvider.getOne(resource, { id })).data)) };
  },
  getManyReference: (resource, params) => {
    resourceName(resource);
    return taxonomyProvider.getList(resource, { ...params, filter: { ...params.filter, [params.target]: params.id } });
  },
  create: async (resource, params) => {
    resourceName(resource);
    const taxonomyKey = params.data.taxonomyKey;
    key(taxonomyKey);
    if (resource === 'taxonomy_terms') key(params.data.code);
    const endpoint = resource === 'taxonomies' ? 'taxonomies' : `taxonomies/${key(taxonomyKey)}/terms`;
    const data = { ...body(params.data), ...(resource === 'taxonomies' ? { taxonomyKey } : { code: params.data.code }) };
    const item = await apiFetch(endpoint, { method: 'POST', body: JSON.stringify(data) });
    return { data: normalise(resource, item, taxonomyKey) as never };
  },
  update: async (resource, params) => {
    resourceName(resource);
    const item = await apiFetch(path(resource, params.id), { method: 'PUT', body: JSON.stringify(body(params.data)) });
    return { data: normalise(resource, item, resource === 'taxonomy_terms' ? termIdentity(params.id)[0] : undefined) as never };
  },
  delete: async (resource, params) => {
    resourceName(resource);
    await apiFetch(path(resource, params.id), { method: 'DELETE' });
    return { data: { id: params.id } as never };
  },
  updateMany: async (resource, params) => {
    resourceName(resource);
    await Promise.all(params.ids.map(id => taxonomyProvider.update(resource, { id, data: params.data, previousData: { id } })));
    return { data: params.ids };
  },
  deleteMany: async (resource, params) => {
    resourceName(resource);
    await Promise.all(params.ids.map(id => taxonomyProvider.delete(resource, { id })));
    return { data: params.ids };
  },
};
